import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
  UseMutationResult,
} from 'react-query'
import { SitesApi } from '@trybeapp/sdk.shop'
import {
  CreateIntegrationConfigRequest,
  IntegrationConfigResponse,
  IntegrationConfigsApi,
  ResponseError,
} from '@trybeapp/sdk'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { apiConfiguration } from 'utilities/TrybeApiClient'

const api = new SitesApi()
const integrationsApi = new IntegrationConfigsApi(apiConfiguration)

export const useGetIntegrationConfigs = (params: any = {}, options = {}) => {
  const siteId = useCurrentSiteId()

  return useQuery(
    ['integrationConfig', { ...params, siteId }],
    () =>
      integrationsApi.listIntegrationConfigs({
        siteId,
        provider: params.provider,
        type: params.type,
        page: params.page,
      }),
    options
  )
}

export const useGetIntegrationConfig = (id: string, options = {}) => {
  return useQuery(
    ['integrationConfig', id],
    () => integrationsApi.getIntegrationConfig({ integrationConfigId: id }),
    options
  )
}

export const useCreateIntegrationConfig = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation<IntegrationConfigResponse, ResponseError, CreateIntegrationConfigRequest>(
    (config) => {
      config.site_id = siteId

      return integrationsApi.createIntegrationConfig({
        createIntegrationConfigRequest: config,
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('integrationConfig')
      },
    }
  )
}

export const useUpdateIntegrationConfig = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ([configId, config]: [string, any]) =>
      integrationsApi.updateIntegrationConfig({
        integrationConfigId: configId,
        updateIntegrationConfigRequest: config,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('integrationConfig')
      },
    }
  )
}

export const useEnableIntegrationConfig = () => {
  const queryClient = useQueryClient()

  return useMutation(
    (configId: string) =>
      integrationsApi.actionEnableIntegration({
        integrationConfigId: configId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('integrationConfig')
      },
    }
  )
}

export const useDisableIntegrationConfig = () => {
  const queryClient = useQueryClient()

  return useMutation(
    (configId: string) =>
      integrationsApi.actionDisableIntegration({
        integrationConfigId: configId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('integrationConfig')
      },
    }
  )
}

export const useGetGuestlineConfig = (options?: UseQueryOptions): UseQueryResult<any> => {
  const siteId = useCurrentSiteId()

  return useQuery(
    ['guestlineConfig', siteId],
    async () => await api.siteGuestlineConfigIndex(siteId),
    options
  )
}

export const useGetGuestlinePaymentCodes = (options?: UseQueryOptions): UseQueryResult<any> => {
  const siteId = useCurrentSiteId()

  return useQuery(
    ['guestlinePaymentCodes', siteId],
    async () => await api.siteGuestlinePaymentCodesIndex(siteId),
    options
  )
}

export const useGetGuestlineAnalysisCodes = (options?: UseQueryOptions): UseQueryResult<any> => {
  const siteId = useCurrentSiteId()

  return useQuery(
    ['guestlineAnalysisCodes', siteId],
    async () => await api.siteGuestlineAnalysisCodesIndex(siteId),
    options
  )
}

export const useUpdateGuestlinePaymentTypeMapping = (): UseMutationResult<any, any> => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(
    async ([customPaymentTypeid, paymentCode]) =>
      await api.siteGuestlinePaymentTypeMappingUpdate(siteId, customPaymentTypeid, {
        updateGuestlinePaymentTypeMapping: { payment_code: paymentCode },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('guestlineConfig')
      },
    }
  )
}

export const useUpdateGuestlineRevenueCentreMapping = (): UseMutationResult<any, any> => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(
    async ([revenueCentreId, analysisCode]) =>
      await api.siteGuestlineRevenueCentreMappingUpdate(siteId, revenueCentreId, {
        updateGuestlineRevenueCentreMapping: { analysis_code: analysisCode },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('guestlineConfig')
      },
    }
  )
}

export const useUpdateGuestlineSettings = (): UseMutationResult<any, any> => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(
    async (settings) =>
      await api.siteGuestlineSettingsUpdate(siteId, {
        guestlineSettings: settings,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('guestlineConfig')
      },
    }
  )
}

export const useUpdateGuestlineCredentials = (): UseMutationResult<any, any> => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(
    async (credentials) =>
      await api.siteGuestlineCredentialsUpdate(siteId, {
        guestlineCredentials: credentials,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('guestlineConfig')
      },
    }
  )
}

export const useEnableGuestline = (): UseMutationResult<any, any> => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(async () => await api.siteGuestlineEnable(siteId), {
    onSuccess: () => {
      queryClient.invalidateQueries('guestlineConfig')
    },
  })
}

export const useDisableGuestline = (): UseMutationResult<any, any> => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(async () => await api.siteGuestlineDisable(siteId), {
    onSuccess: () => {
      queryClient.invalidateQueries('guestlineConfig')
    },
  })
}

export const useUpdatePmsSettings = (): UseMutationResult<any, any> => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(
    async (settings) =>
      await api.sitePmsSettingsUpdate(siteId, {
        pmsConfig: settings,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('guestlineConfig')
        queryClient.invalidateQueries('mewsConfig')
        queryClient.invalidateQueries('operaFiasConfig')
      },
    }
  )
}

export const useGetGiftProConfig = (options?: UseQueryOptions): UseQueryResult<any> => {
  const siteId = useCurrentSiteId()

  return useQuery(
    ['giftProConfig', siteId],
    async () => await api.siteGiftProConfigIndex(siteId),
    options
  )
}

export const useEnableGiftPro = (): UseMutationResult<any, any> => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(async () => await api.siteGiftProEnable(siteId), {
    onSuccess: () => {
      queryClient.invalidateQueries('giftProConfig')
    },
  })
}

export const useDisableGiftPro = (): UseMutationResult<any, any> => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(async () => await api.siteGiftProDisable(siteId), {
    onSuccess: () => {
      queryClient.invalidateQueries('giftProConfig')
    },
  })
}

export const useUpdateGiftProSettings = (): UseMutationResult<any, any> => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(
    async (settings) =>
      await api.siteGiftProSettingsUpdate(siteId, {
        updateGiftProSettings: settings,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('giftProConfig')
      },
    }
  )
}

export const useGetAvvioConfig = (options?: UseQueryOptions): UseQueryResult<any> => {
  const siteId = useCurrentSiteId()

  return useQuery(
    ['avvioConfig', siteId],
    async () => await api.siteAvvioConfigIndex(siteId),
    options
  )
}

export const useEnableAvvio = (): UseMutationResult<any, any> => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(async () => await api.siteAvvioEnable(siteId), {
    onSuccess: () => {
      queryClient.invalidateQueries('avvioConfig')
    },
  })
}

export const useDisableAvvio = (): UseMutationResult<any, any> => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(async () => await api.siteAvvioDisable(siteId), {
    onSuccess: () => {
      queryClient.invalidateQueries('avvioConfig')
    },
  })
}

export const useUpdateAvvioSettings = (): UseMutationResult<any, any> => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(
    async (settings) =>
      await api.siteAvvioSettingsUpdate(siteId, {
        updateAvvioSettings: settings,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('avvioConfig')
      },
    }
  )
}

export const useImportAvvioRoomTypes = (): UseMutationResult<any, any> => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(async () => await api.siteAvvioSettingsImportRoomTypes(siteId), {
    onSuccess: () => {
      queryClient.invalidateQueries('avvioConfig')
    },
  })
}

export const useGetBookingEngineRates = (options?: UseQueryOptions): UseQueryResult<any> => {
  const siteId = useCurrentSiteId()

  return useQuery(
    ['bookingEngineRates', siteId],
    async () => await api.siteSettingsBookingEngineRatesIndex(siteId),
    options
  )
}

export const useGetBookingEngineAgeCategories = (
  options?: UseQueryOptions
): UseQueryResult<any> => {
  const siteId = useCurrentSiteId()

  return useQuery(
    ['bookingEngineAgeCategories', { siteId }],
    () => api.siteSettingsBookingEngineAgeCategoriesIndex(siteId),
    options
  )
}
